import React, { useEffect, useRef, useState } from "react"

interface WeeklyPrioritySelectorProps {
  taskId: string
  currentWeekAssigned?: string | null
  isWeeklyPriority?: boolean
  onWeeklyPriorityChange: (taskId: string, isWeeklyPriority: boolean, weekStart?: Date) => void
}

const WeeklyPrioritySelector: React.FC<WeeklyPrioritySelectorProps> = ({
  taskId,
  currentWeekAssigned,
  isWeeklyPriority = false,
  onWeeklyPriorityChange,
}) => {
  const [showSelector, setShowSelector] = useState(false)
  const selectorRef = useRef<HTMLDivElement>(null)

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowSelector(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Generate next 8 weeks starting from current Sunday
  const getWeekOptions = () => {
    const options: {
      weekStart: Date
      weekEnd: Date
      weekNumber: number
      label: string
      dateRange: string
      isCurrentWeek: boolean
      weekType: "current" | "next" | "future"
      colorClass: string
    }[] = []
    const today = new Date()
    const currentSunday = new Date(today)
    currentSunday.setDate(today.getDate() - today.getDay())
    currentSunday.setHours(0, 0, 0, 0)

    for (let i = 0; i < 8; i++) {
      const weekStart = new Date(currentSunday)
      weekStart.setDate(currentSunday.getDate() + i * 7)

      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)

      // Get week number (approximate)
      const yearStart = new Date(weekStart.getFullYear(), 0, 1)
      const firstSunday = new Date(yearStart)
      firstSunday.setDate(yearStart.getDate() - yearStart.getDay())
      const timeDiff = weekStart.getTime() - firstSunday.getTime()
      const weekNumber = Math.floor(timeDiff / (7 * 24 * 60 * 60 * 1000)) + 1

      // Determine week type and color
      let weekType: "current" | "next" | "future"
      let colorClass: string

      if (i === 0) {
        weekType = "current"
        colorClass = "bg-green-100 border-green-200 text-green-800"
      } else if (i === 1) {
        weekType = "next"
        colorClass = "bg-blue-100 border-blue-200 text-blue-800"
      } else {
        weekType = "future"
        colorClass = "bg-foregroundx border-border text-primary"
      }

      options.push({
        weekStart,
        weekEnd,
        weekNumber,
        weekType,
        colorClass,
        label: i === 0 ? "This Week" : i === 1 ? "Next Week" : `Week ${weekNumber}`,
        dateRange: `${weekStart.getMonth() + 1}/${weekStart.getDate()} - ${
          weekEnd.getMonth() + 1
        }/${weekEnd.getDate()}`,
        isCurrentWeek: currentWeekAssigned === weekStart.toISOString().split("T")[0],
      })
    }

    return options
  }

  const weekOptions = getWeekOptions()

  const handleWeekSelect = (weekStart: Date) => {
    onWeeklyPriorityChange(taskId, true, weekStart)
    setShowSelector(false)
  }

  const handleRemoveWeeklyPriority = () => {
    onWeeklyPriorityChange(taskId, false)
    setShowSelector(false)
  }

  // Format current week assignment for display
  const getCurrentWeekDisplay = () => {
    if (!isWeeklyPriority || !currentWeekAssigned) return null

    const assignedDate = new Date(currentWeekAssigned)
    const weekOption = weekOptions.find(
      (option) => option.weekStart.toISOString().split("T")[0] === currentWeekAssigned,
    )

    if (weekOption) {
      return {
        text: `W${weekOption.weekNumber} - ${weekOption.dateRange}`,
        colorClass: weekOption.colorClass,
        weekType: weekOption.weekType,
      }
    }

    // Fallback for dates outside the current 8-week range
    const month = assignedDate.getMonth() + 1
    const day = assignedDate.getDate()
    return {
      text: `${month}/${day}`,
      colorClass: "bg-foregroundx text-primary",
      weekType: "future" as const,
    }
  }

  const currentWeekDisplay = getCurrentWeekDisplay()

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation()
          setShowSelector(!showSelector)
        }}
        className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded transition-colors border ${
          isWeeklyPriority && currentWeekAssigned && currentWeekDisplay
            ? `${currentWeekDisplay.colorClass} hover:opacity-80`
            : "bg-foregroundx text-gray-500  border-border"
        }`}
        title={isWeeklyPriority ? "Change weekly priority assignment" : "Assign to weekly priorities"}
      >
        📅 {currentWeekDisplay?.text || "No week assigned"}
        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Week selector popover */}
      {showSelector && (
        <div
          ref={selectorRef}
          className="absolute top-full left-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-3 z-50 w-64"
        >
          <div className="space-y-3">
            <div className="text-sm font-medium text-primary">Assign to weekly priority:</div>

            {/* Color legend */}
            <div className="flex items-center gap-4 text-xs  pb-2 border-b border-gray-100">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span>This week</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Next week</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                <span>Future</span>
              </div>
            </div>

            {/* Week options */}
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {weekOptions.map((option) => (
                <button
                  key={option.weekStart.toISOString()}
                  onClick={() => handleWeekSelect(option.weekStart)}
                  className={`w-full text-left p-2 rounded-lg transition-colors hover:opacity-80 border ${
                    option.isCurrentWeek
                      ? `${option.colorClass} ring-2 ring-offset-1 ring-yellow-400`
                      : `${option.colorClass} hover:shadow-sm`
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Color indicator dot */}
                      <div
                        className={`w-2 h-2 rounded-full ${
                          option.weekType === "current"
                            ? "bg-green-500"
                            : option.weekType === "next"
                              ? "bg-blue-500"
                              : "bg-gray-400"
                        }`}
                      />
                      <div>
                        <div className="text-sm font-medium">
                          {option.label}
                          {option.isCurrentWeek && <span className="ml-2 text-xs opacity-75">(assigned)</span>}
                        </div>
                        <div className="text-xs opacity-75">{option.dateRange}</div>
                      </div>
                    </div>
                    <div className="text-xs opacity-60">W{option.weekNumber}</div>
                  </div>
                </button>
              ))}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-2 border-t border-border">
              {isWeeklyPriority && (
                <button
                  onClick={handleRemoveWeeklyPriority}
                  className="px-3 py-1.5 text-sm text-destructive hover:text-destructive transition-colors"
                >
                  Remove from weekly priorities
                </button>
              )}
              <button
                onClick={() => setShowSelector(false)}
                className="px-3 py-1.5 text-sm  hover:text-gray-800 transition-colors ml-auto"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WeeklyPrioritySelector
